import React, { useCallback, useMemo, useState } from 'react';
import { Box, Checkbox } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { IManager } from '@sr/dto';

export const TOGGLE_HIDDEN_ID = -1;
export const HIDDEN_TOGGLE_LABEL = 'Показывать неактивных и уволенных';

export type ManagerStatusFields = {
  isActive?: number | boolean | null;
  isFired?: number | boolean | null;
};

export type ManagerWithStatus = IManager & ManagerStatusFields;

export const filterManagerOptions = createFilterOptions<IManager>();

export const getManagerStatusSuffix = (
  manager: Partial<ManagerWithStatus> | null | undefined
): string | null => {
  if (!manager || manager.id === 0 || manager.id == null) return null;
  if (manager.isFired) return '(уволен)';
  if (!manager.isActive) return '(неактив.)';
  return null;
};

/**
 * «Скрываемый» менеджер — это уволенный или неактивный.
 * Уволенные и неактивные по умолчанию не отображаются в выпадающем списке
 * и показываются только при включённом тумблере.
 */
export const isHiddenManager = (
  manager: Partial<ManagerWithStatus> | null | undefined
): boolean => {
  if (!manager) return false;
  if (manager.id === 0 || manager.id == null) return false;
  return !!manager.isFired || !manager.isActive;
};

export const isToggleHiddenOption = (option?: { id?: number } | null): boolean =>
  !!option && option.id === TOGGLE_HIDDEN_ID;

const createToggleOption = (): IManager =>
  ({
    firstName: '__toggle_hidden__',
    secondName: '',
    id: TOGGLE_HIDDEN_ID
  } as unknown as IManager);

export const filterHiddenManagers = (
  items: IManager[],
  showHidden: boolean
): IManager[] =>
  showHidden
    ? items
    : items.filter((m) => !isHiddenManager(m as ManagerStatusFields));

export const withHiddenToggle = (
  items: IManager[],
  { hasHiddenManagers }: { hasHiddenManagers: boolean }
): IManager[] => (hasHiddenManagers ? [createToggleOption(), ...items] : items);

export const useHiddenManagerToggleState = (items: IManager[]) => {
  const [showHidden, setShowHidden] = useState(false);
  const toggleShowHidden = useCallback(() => setShowHidden((p) => !p), []);
  const hasHiddenManagers = useMemo(
    () => items.some((m) => isHiddenManager(m as ManagerStatusFields)),
    [items]
  );

  return { showHidden, setShowHidden, toggleShowHidden, hasHiddenManagers };
};

export const splitToggleOptions = (
  options: IManager[],
  params: Parameters<typeof filterManagerOptions>[1]
): IManager[] => {
  const toggles = options.filter((o) => isToggleHiddenOption(o));
  const rest = options.filter((o) => !isToggleHiddenOption(o));
  return [...toggles, ...filterManagerOptions(rest, params)];
};

type HiddenToggleOptionProps = React.ComponentPropsWithoutRef<'li'> & {
  showHidden: boolean;
  onToggle: () => void;
  label?: string;
};

export const HiddenToggleOption = ({
  showHidden,
  onToggle,
  label = HIDDEN_TOGGLE_LABEL,
  ...rest
}: HiddenToggleOptionProps) => (
  <Box
    component="li"
    {...rest}
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      cursor: 'pointer',
      borderBottom: '1px solid',
      borderColor: 'divider',
      position: 'sticky',
      top: 0,
      bgcolor: 'background.paper',
      zIndex: 1
    }}
  >
    <Checkbox
      checked={showHidden}
      size="small"
      disableRipple
      tabIndex={-1}
      sx={{ p: 0.5, pointerEvents: 'none' }}
    />
    <Box component="span" sx={{ color: 'text.secondary', fontSize: 14 }}>
      {label}
    </Box>
  </Box>
);

type RenderManagerOptionCtx = {
  showHidden: boolean;
  onToggle: () => void;
  getLabel?: (option: IManager) => string;
};

export const renderManagerOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
  option: IManager,
  { showHidden, onToggle, getLabel }: RenderManagerOptionCtx
): React.ReactElement => {
  const { key, ...rest } = optionProps;

  if (isToggleHiddenOption(option)) {
    return (
      <HiddenToggleOption
        key={key}
        {...rest}
        showHidden={showHidden}
        onToggle={onToggle}
      />
    );
  }

  const label = getLabel
    ? getLabel(option)
    : `${option.firstName ?? ''} ${option.secondName ?? ''}`.trim();
  const suffix = getManagerStatusSuffix(option);

  return (
    <Box
      component="li"
      key={key}
      {...rest}
      sx={{ display: 'flex', alignItems: 'baseline' }}
    >
      <Box
        component="span"
        sx={{ color: suffix ? 'text.disabled' : 'inherit' }}
      >
        {label}
      </Box>
      {suffix && (
        <Box component="span" sx={{ ml: 0.75, color: 'text.disabled' }}>
          {suffix}
        </Box>
      )}
    </Box>
  );
};